import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Bell, Plus, Trash2, X, ToggleLeft, ToggleRight, Pencil, AlertTriangle, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SQUADS = ['U15', 'U17', 'U19', 'U21', 'Senior', 'Reserve'];
const SEVERITIES = ['High', 'Medium', 'Low'];
const SEV_COLOR = {
  High: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
  Low: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
};

const AlertConfig = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'Admin';

  const [thresholds, setThresholds] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    squad: '', metric: '', operator: 'below', value: '', severity: 'Medium', enabled: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [tRes, mRes] = await Promise.all([
        axios.get(`${API}/api/alert-config`, { headers }),
        axios.get(`${API}/api/alert-config/metrics`, { headers }),
      ]);
      setThresholds(tRes.data || []);
      setMetrics(mRes.data?.metrics || []);
    } catch {
      toast.error('Failed to load alert configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ squad: '', metric: '', operator: 'below', value: '', severity: 'Medium', enabled: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (t) => {
    setForm({ squad: t.squad, metric: t.metric, operator: t.operator, value: t.value, severity: t.severity, enabled: t.enabled });
    setEditing(t.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.squad || !form.metric || !form.value) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) };
      if (editing) {
        await axios.put(`${API}/api/alert-config/${editing}`, payload, { headers });
        toast.success('Threshold updated');
      } else {
        await axios.post(`${API}/api/alert-config`, payload, { headers });
        toast.success('Threshold created');
      }
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (t) => {
    try {
      await axios.put(`${API}/api/alert-config/${t.id}`, { enabled: !t.enabled }, { headers });
      setThresholds(prev => prev.map(x => x.id === t.id ? { ...x, enabled: !x.enabled } : x));
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this threshold rule?')) return;
    try {
      await axios.delete(`${API}/api/alert-config/${id}`, { headers });
      setThresholds(prev => prev.filter(x => x.id !== id));
      toast.success('Threshold deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getMetricLabel = (key) => metrics.find(m => m.key === key)?.label || key;

  // Group by squad
  const grouped = {};
  thresholds.forEach(t => {
    if (!grouped[t.squad]) grouped[t.squad] = [];
    grouped[t.squad].push(t);
  });

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl" data-testid="alert-config-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="page-title">
              <Bell size={22} className="inline mr-2 mb-1 text-pfap-green" />
              Alert Rules
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Configure performance thresholds per squad. Auto-triggers flags when breached.
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="add-rule-btn">
              <Plus size={16} className="mr-1.5" /> Add Rule
            </Button>
          )}
        </div>

        {/* Create/Edit Form */}
        {showForm && isAdmin && (
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="threshold-form">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-gray-900 dark:text-gray-100">
                  {editing ? 'Edit Threshold' : 'New Threshold Rule'}
                </CardTitle>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 text-xs">Squad *</Label>
                  <select value={form.squad} onChange={e => setForm(p => ({ ...p, squad: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                    data-testid="form-squad">
                    <option value="">Select Squad</option>
                    {SQUADS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 text-xs">Metric *</Label>
                  <select value={form.metric} onChange={e => setForm(p => ({ ...p, metric: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                    data-testid="form-metric">
                    <option value="">Select Metric</option>
                    {metrics.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 text-xs">Condition *</Label>
                  <select value={form.operator} onChange={e => setForm(p => ({ ...p, operator: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                    data-testid="form-operator">
                    <option value="below">Falls Below</option>
                    <option value="above">Exceeds</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 text-xs">Value *</Label>
                  <Input type="number" step="0.1" value={form.value}
                    onChange={e => setForm(p => ({ ...p, value: e.target.value }))}
                    placeholder="e.g. 6.0" className="h-9" data-testid="form-value" />
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 text-xs">Severity *</Label>
                  <select value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                    data-testid="form-severity">
                    {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleSave} disabled={saving} className="bg-pfap-green hover:bg-pfap-green/90 text-white h-9 w-full" data-testid="form-save-btn">
                    {saving ? 'Saving...' : editing ? 'Update' : 'Create Rule'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Thresholds by Squad */}
        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pfap-green" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="py-16 text-center">
              <Shield size={36} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-400 dark:text-gray-500" data-testid="no-rules-msg">No alert rules configured yet</p>
              {isAdmin && <p className="text-xs text-gray-400 mt-1">Click "Add Rule" to create your first threshold</p>}
            </CardContent>
          </Card>
        ) : (
          Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([squad, rules]) => (
            <Card key={squad} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid={`squad-group-${squad}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {squad}
                  <Badge className="ml-2 text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500">{rules.length} rule{rules.length > 1 ? 's' : ''}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rules.map(t => (
                    <div key={t.id} className={`flex items-center justify-between py-2.5 ${!t.enabled ? 'opacity-50' : ''}`} data-testid={`rule-${t.id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Badge className={`text-[10px] border ${SEV_COLOR[t.severity]}`}>{t.severity}</Badge>
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            <span className="font-medium">{getMetricLabel(t.metric)}</span>
                            <span className="text-gray-400 mx-1.5">{t.operator === 'below' ? 'falls below' : 'exceeds'}</span>
                            <span className="font-semibold text-pfap-green">{t.value}</span>
                          </p>
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => handleToggle(t)} className="p-1 text-gray-400 hover:text-gray-600" data-testid={`toggle-${t.id}`} title={t.enabled ? 'Disable' : 'Enable'}>
                            {t.enabled ? <ToggleRight size={20} className="text-pfap-green" /> : <ToggleLeft size={20} />}
                          </button>
                          <button onClick={() => handleEdit(t)} className="p-1 text-gray-400 hover:text-blue-600" data-testid={`edit-${t.id}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDelete(t.id)} className="p-1 text-gray-400 hover:text-red-600" data-testid={`delete-${t.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {/* Info box */}
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" data-testid="info-box">
          <AlertTriangle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700 dark:text-blue-300">
            Alerts are automatically evaluated when new match reports are submitted. You can also manually trigger evaluation from an athlete's profile.
            Triggered alerts appear as flags in the Alerts & Flags module.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default AlertConfig;
